import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  Divider,
  LinearProgress,
  Theme,
  Typography,
} from "@mui/material";
import type { SxProps } from "@mui/system";
import { useRouter } from "next/router";
import propTypes from "prop-types";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAuth } from "src/hooks/use-auth";
import { snackAdded } from "src/slices/system";
import { selectCurrentPlan } from "src/slices/system/selectors";
import {
  changeCurrentPlan,
  getCheckoutLink,
  getPlanDetails,
  getTeamMembers,
} from "src/slices/system/thunks";
import { useDispatch } from "src/store";
import { Check as CheckIcon } from "../../icons/check";

interface PricingPlanProps {
  cta?: string;
  currency: string;
  description: string;
  features?: any[];
  image: string;
  name: string;
  popular?: boolean;
  afterPrice?: string;
  price: string;
  priceDescription?: string;
  sx?: SxProps<Theme>;
  monthlyPriceId: string;
  isActive?: boolean;
  paddlePlanId?: string;
  displayFeatures?: string[];
  index?: number;
  planType?: string;
}

export interface CustomWindow extends Window {
  FPROM: any;
  Paddle: any;
}

declare let window: CustomWindow;

export const PricingPlan: FC<PricingPlanProps> = (props) => {
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [changePlanName, setChangePlanName] = useState("");
  const [changePlanTenure, setChangePlanTenure] = useState("");
  const [changePlanPrice, setChangePlanPrice] = useState("");
  const [currentTeamMembers, setCurrentTeamMembers] = useState(0);
  const currentPlan = useSelector(selectCurrentPlan);
  const { user } = useAuth();

  const {
    cta,
    currency,
    description,
    displayFeatures,
    image,
    name,
    popular,
    price,
    sx,
    monthlyPriceId,
    isActive,
    priceDescription,
    afterPrice,
    paddlePlanId,
    planType,
    index,
    ...other
  } = props;
  let callToAction = cta;
  if ((name === "Pro" || name === "Basic") && !user?.isTrialUsed) {
    callToAction = "Free Trial";
  }

  const dispatch = useDispatch();
  const router = useRouter();
  useEffect(() => {
    const TeamMembers = async () => {
      const members = await dispatch(
        getTeamMembers({
          email: user?.email,
        })
      ).unwrap();
      if (members?.teamMembers?.length > 0) {
        setCurrentTeamMembers(members?.teamMembers?.length + 1);
      } else {
        setCurrentTeamMembers(1);
      }
    };
    TeamMembers();
  }, []);

  const isDowngrading = (planDetails: any) => {
    if (currentPlan?.subscription_id && !user?.plan?.isCancelled) {
      let unitPrice = Number(planDetails[0]?.recurring_price.USD);
      let currentUnitPrice = Number(currentPlan?.unit_price);
      return (
        currentUnitPrice > unitPrice &&
        currentTeamMembers > Number(currentPlan?.teamMembers)
      );
    }
    return false;
  };
  const handleSubscribe = async (isconfirmed = false) => {
    if (currentPlan?.subscription_id && !user?.plan?.isCancelled) {
      if (!isconfirmed) {
        const PlanDetails = await dispatch(
          getPlanDetails({ planId: paddlePlanId })
        ).unwrap();
        const isDowngrade = isDowngrading(PlanDetails);

        //if (isDowngrade) {
        //  dispatch(
        //    snackAdded({
        //      message: `You cannot downgrade your plan because current team members ${currentTeamMembers} are more than the new plan allows ${currentPlan?.teamMembers}. Please remove some team members or upgrade to a higher plan.`,
        //      type: "error",
        //      duration: 10000,
        //    })
        //  );
        //  return;
        //}
        setChangePlanName(PlanDetails[0]?.name);
        setChangePlanTenure(PlanDetails[0]?.billing_type);
        setChangePlanPrice(PlanDetails[0]?.recurring_price.USD);
        setShowConfirmationModal(true);
      } else {
        setShowConfirmationModal(false);
        setShowProcessingModal(true);
        try {
          const result = await dispatch(
            changeCurrentPlan({ planId: paddlePlanId })
          ).unwrap();
          if (result.plan_id) {
            dispatch(
              snackAdded({
                message: "Transaction Completed Successfully",
                type: "success",
                duration: 3000,
              })
            );
            router.push("/dashboard");
          }
        } catch (error) {
          dispatch(
            snackAdded({
              message: "Error",
              type: "error",
              duration: 3000,
            })
          );
        }

        setShowProcessingModal(false);
      }
    } else {
      // if plan name is pro or basic

      let checkoutObj = {};
      if ((name === "Pro" || name === "Basic") && !user?.isTrialUsed) {
        const link = await dispatch(
          getCheckoutLink({ planId: paddlePlanId })
        ).unwrap();

        //add override : link to checkoutObj
        checkoutObj = {
          product: paddlePlanId,
          allowQuantity: false,
          disableLogout: true,
          email: user.email,
          success: `${window.location.origin}/app/dashboard/pricing?success=true`,
          override: link,
        };
      } else {
        checkoutObj = {
          product: paddlePlanId,
          allowQuantity: false,
          disableLogout: true,
          email: user.email,
          success: `${window.location.origin}/app/dashboard/pricing?success=true`,
        };
      }

      window.Paddle?.Checkout.open(checkoutObj);
    }
  };

  const handleClose = () => {
    setShowProcessingModal(false);
    setShowConfirmationModal(false);
  };
  const returnCssForButton = (index) => {
    const commonStyle = {
      borderRadius: "5px",
      padding: "0.8em 54px",
      fontWeight: "400",
      fontSize: "0.9rem",
      "&:hover": {
        background: "#080548",
        color: "#fff",
      },
    };
    if (index === 1) {
      return {
        background: "#fff",
        color: "#000",
        ...commonStyle,
      };
    } else {
      return {
        color: "#fff",
        background: "linear-gradient(to bottom right,#9800e9,#6a00ff)",
        ...commonStyle,
        // disabled css
        "&:disabled": {
          color: "#fff",
        },
      };
    }
  };
  const commonHighlightStyle = () => {
    if (index === 1) {
      return { color: "#fff" };
    } else {
      return {};
    }
  };
  const getHighlightStyles = () => {
    if (index === 1) {
      return {
        background: "#6a00ff",
        color: "#fff",
      };
    }
    return {};
  };
  const getHighlightStyleForPlanName = () => {
    if (index === 1) {
      return {
        color: "#fff",
      };
    }
    return {
      color: "#60f",
      background: "linear-gradient(to bottom right,#f09,#60f)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    };
  };
  return (
    <>
      <Dialog onClose={handleClose} open={showProcessingModal}>
        <DialogTitle>Payment processing</DialogTitle>
        <LinearProgress />
      </Dialog>
      <Dialog onClose={handleClose} open={showConfirmationModal}>
        <Box width="464px">
          <Box
            display="flex"
            width="464px "
            height="526px"
            paddingTop="8px"
            flexDirection="column"
            justifyContent="flex-end"
            alignItems="center"
            sx={{
              position: "absolute",
              width: "100%",
              height: "8px",
              borderRadius: "4px 4px 0px 0px",
              background:
                "linear-gradient(60.66deg, #FFD875 -3.87%, #DA00C8 26.08%, #6A00FF 56.49%)",
            }}
          />

          <Box
            sx={{
              borderRadius: "0px 0px 8px 8px",
              background: "#FFFFFF",

              /* Wrapper Shadow */
              boxShadow: "0px 1px 2px 0px rgba(100, 116, 139, 0.10)",
            }}
            display="flex"
            padding="24px"
            flexDirection="column"
            alignItems="flex-start"
            gap="16px"
            alignSelf="stretch"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              alignSelf="stretch"
            >
              <Typography
                color="#121828"
                /* Heading/H1 */
                fontFamily="Inter"
                fontSize="20px"
                fontStyle="normal"
                fontWeight="500"
                lineHeight="normal"
              >
                Upgrade Your Subscription
              </Typography>
              <Typography
                width="19.267px"
                height="19.267px"
                flexShrink="0"
                sx={{
                  cursor: "pointer",
                }}
                onClick={() => handleClose()}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M2.69203 15.6063L8.66481 9.6335L2.69203 3.66072C2.46324 3.43568 2.33378 3.12787 2.33228 2.80725C2.33153 2.48588 2.45872 2.17807 2.686 1.95077C2.91329 1.72423 3.2211 1.59705 3.54248 1.59855C3.86385 1.60006 4.17091 1.72951 4.39595 1.95906L10.3664 7.93183L16.3392 1.95906C16.5642 1.72951 16.8713 1.60006 17.1927 1.59855C17.5133 1.59705 17.8218 1.72424 18.0491 1.95077C18.2764 2.17807 18.4036 2.48588 18.4021 2.80725C18.4014 3.12787 18.2719 3.43568 18.0431 3.66072L12.068 9.6335L18.0408 15.6063C18.2696 15.8313 18.399 16.1391 18.4005 16.4597C18.4013 16.7811 18.2741 17.0889 18.0468 17.3162C17.8195 17.5428 17.5117 17.6699 17.1903 17.6684C16.869 17.6669 16.5619 17.5375 16.3369 17.3079L10.3664 11.3352L4.39364 17.3079C3.9225 17.7723 3.16537 17.7693 2.69723 17.3027C2.22986 16.8353 2.2261 16.0782 2.68971 15.6063L2.69203 15.6063Z"
                    fill="#7B85AD"
                  />
                </svg>
              </Typography>
            </Box>
            <Divider
              sx={{
                width: 416,
                height: 1,
                background: "#E6E8F0",
              }}
            />
            <Box alignSelf="stretch">
              <Typography
                color="#121828"
                fontFamily="Inter"
                fontSize="16px"
                fontStyle="normal"
                fontWeight="400"
                lineHeight="24px" /* 150% */
              >
                You're about to upgrade your subscription plan. Below you can
                find your order details.
              </Typography>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              gap="8px"
              alignSelf="stretch"
              padding="8px 16px"
              sx={{
                borderRadius: "8px",
                background: "#F9FAFC",
              }}
            >
              <Typography
                color="#121828"
                fontFamily="Inter"
                fontSize="14px"
                fontStyle="normal"
                fontWeight="600"
                lineHeight="normal"
              >
                Plan
              </Typography>
              <Typography
                color="#5048E5"
                fontFamily="Inter"
                fontSize="20px"
                fontStyle="normal"
                fontWeight="600"
                lineHeight="normal"
              >
                {changePlanName}
              </Typography>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              gap="8px"
              alignSelf="stretch"
              padding="8px 16px"
              sx={{
                borderRadius: "8px",
                background: "#F9FAFC",
              }}
            >
              <Typography
                color="#121828"
                fontFamily="Inter"
                fontSize="14px"
                fontStyle="normal"
                fontWeight="600"
                lineHeight="normal"
              >
                Billing cycle
              </Typography>
              <Typography
                color="#5048E5"
                fontFamily="Inter"
                fontSize="20px"
                fontStyle="normal"
                fontWeight="600"
                lineHeight="normal"
              >
                {changePlanTenure === "month" ? "Monthly" : "Yearly"}
              </Typography>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              gap="8px"
              alignSelf="stretch"
              padding="8px 16px"
              sx={{
                borderRadius: "8px",
                background: "#F9FAFC",
              }}
            >
              <Typography
                color="#121828"
                fontFamily="Inter"
                fontSize="14px"
                fontStyle="normal"
                fontWeight="600"
                lineHeight="normal"
              >
                Net price (billed today)
              </Typography>
              <Typography
                color="#5048E5"
                fontFamily="Inter"
                fontSize="20px"
                fontStyle="normal"
                fontWeight="600"
                lineHeight="normal"
              >
                ${changePlanPrice}
              </Typography>
            </Box>

            <Divider
              sx={{
                width: 416,
                height: 1,
                background: "#E6E8F0",
              }}
            />

            <Box display="flex" gap="32px" alignItems="center">
              <Button
                variant="contained"
                color="primary"
                // onClick={}
                size="large"
                // disabled={isLoading || selected.length <= 0}
                sx={{
                  mr: 1,
                  width: "183px",
                  display: "flex",

                  padding: "8px 0px",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "8px",
                  background: "#5048E5",
                }}
                onClick={() => handleSubscribe(true)}
              >
                Pay Now
              </Button>

              <Typography
                color=" #7B85AD"
                textAlign="center"
                fontFamily="Inter"
                fontSize="12px"
                fontStyle="normal"
                fontWeight="600"
                lineHeight="normal"
                sx={{
                  cursor: "pointer",
                }}
                onClick={() => handleClose()}
              >
                Change Plan
              </Typography>
            </Box>
            <Divider
              sx={{
                width: 416,
                height: 1,
                background: "#E6E8F0",
              }}
            />

            <Box alignSelf="stretch">
              <Typography
                color=" #7B85AD"
                fontFamily="Inter"
                fontSize="12px"
                fontStyle="normal"
                fontWeight="400"
                lineHeight="normal"
              >
                By clicking "Pay now," you are buying the plan and will be
                automatically charged.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Dialog>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 30px 60px rgba(0,0,0,.15)",
          borderRadius: "8px",
          ...getHighlightStyles(),
          ...sx,
        }}
        {...other}
      >
        <Box sx={{ py: 3, px: 5 }}>
          <Box
            sx={{
              display: "flex",
              // justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mb: 2,
            }}
          >
            <Typography
              sx={{
                // mb: 2,
                fontSize: "40px",
                fontWeight: "800",
                lineHeight: "48px",
                ...getHighlightStyleForPlanName(),
              }}
            >
              {name}
            </Typography>
            {/* {name === "Pro" && (
              <Typography
                sx={{
                  color: "#333",
                  backgroundColor: "#f9f9f9",
                  borderRadius: ".375rem",
                  padding: ".35rem .5rem",
                  fontWeight: "600",
                  display: "inline-block",
                  fontSize: "11px",
                }}
              >
                ⚡LIMITED OFFER: SAVE{" "}
                <strong>{planType == "yearly" ? "50%" : "25%"}</strong>
              </Typography>
            )} */}
          </Box>
          <Box sx={{ display: "flex" }}>
            {/* {name === "Pro" && (
              <>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "500",
                    color: "#333",
                    ...commonHighlightStyle(),
                    //strike through
                    textDecoration: "line-through",
                  }}
                >
                  {currency}
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    color: "#333",
                    mr: 1,
                    ...commonHighlightStyle(),
                    //strike through
                    textDecoration: "line-through",
                  }}
                >
                  119
                </Typography>
              </>
            )} */}

            <Typography
              variant="h4"
              sx={{
                fontWeight: "500",
                color: "#333",
                ...commonHighlightStyle(),
              }}
            >
              {currency}
            </Typography>

            <Typography
              variant="h4"
              sx={{
                color: "#333",
                ...commonHighlightStyle(),
              }}
            >
              {price}
            </Typography>

            <Typography
              sx={{
                alignSelf: "flex-end",
                // ml: 1,
                fontWeight: "700",
                fontSize: "12px",
                color: "#333",
                ...commonHighlightStyle(),
              }}
              variant="subtitle2"
            >
              / mo
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                alignSelf: "flex-end",
                ml: 1,
                mt: 1,
                fontWeight: "700",
                fontSize: "14px",
                color: "#65748B",
                ...commonHighlightStyle(),
              }}
            >
              {afterPrice ? afterPrice : `billed ${planType}`}
            </Typography>
          </Box>
          <Typography color="textSecondary">{priceDescription}</Typography>

          <Typography
            color="textSecondary"
            sx={{ mt: 2, ...commonHighlightStyle() }}
            variant="body2"
          >
            {description}
          </Typography>
          <div
            style={{
              textAlign: "center",
              marginTop: "20px",
            }}
          >
            {cta && (
              <Button
                sx={returnCssForButton(index)}
                disabled={isActive && !user?.plan?.isCancelled}
                onClick={() => handleSubscribe()}
              >
                {isActive && !user?.plan?.isCancelled
                  ? "Current plan"
                  : callToAction}
              </Button>
            )}
          </div>
        </Box>
        <Divider />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            p: 3,
          }}
        >
          {displayFeatures.map((feature) => (
            <Box
              key={feature}
              sx={{
                alignItems: "center",
                display: "flex",
                "& + &": {
                  mt: 2,
                },
              }}
            >
              <CheckIcon fontSize="small" sx={{ color: "#da00c8" }} />
              <Typography
                sx={{
                  ml: 2,
                }}
                variant="body2"
              >
                {feature}
              </Typography>
            </Box>
          ))}
          <Box sx={{ flexGrow: 1 }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 6,
            }}
          ></Box>
          <div className="checkout-container" />
        </Box>
      </Box>
    </>
  );
};

PricingPlan.propTypes = {
  cta: propTypes.string.isRequired,
  currency: propTypes.string.isRequired,
  description: propTypes.string.isRequired,
  features: propTypes.array.isRequired,
  planType: propTypes.string.isRequired,
  image: propTypes.string.isRequired,
  name: propTypes.string.isRequired,
  popular: propTypes.bool,
  price: propTypes.string.isRequired,
  index: propTypes.number.isRequired,
  sx: propTypes.object,
};
